// @mui
import {
  Typography,
  MenuItem,
  Box,
  TextFieldProps,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useTheme } from '@mui/material/styles'
import useLocales from 'hooks/useLocales'
// components
import RHFSelect from 'components/hook-form/RHFSelect'

const InputTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 'bold',
  marginBottom: '14px',
})

export interface SelectOption {
  name: string | number
  label: string
}
interface ISelectProps {
  id?: string
  name: string
  label?: string | JSX.Element
  subLabel?: string | JSX.Element
  value: string
  placeholder?: string
  disabled?: boolean
  options: SelectOption[]
  required?: boolean
  onChange: Function
  onFocus?: Function
  sx?: Object
}

const InputRequired = styled('span')(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 'bold',
  color: theme.palette.primary.main,
}))

export default function Select({
  id,
  name,
  label,
  subLabel,
  value,
  placeholder,
  disabled,
  options,
  required,
  onChange,
  onFocus,
  sx,
  ...other
}: ISelectProps & TextFieldProps) {
  const { t } = useLocales()
  const theme = useTheme()
  const disabledStyle = disabled
    ? {
        '&.MuiSelect-select, &.Mui-disabled': {
          backgroundColor: theme.palette.action.disabledBackground,
        },
        '& svg': {
          backgroundColor: 'unset !important',
        },
      }
    : {}

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onChange(event)
  }
  const handleFocus = (e) => onFocus && onFocus(e)

  return (
    <>
      {label && (
        <InputTitle>
          {t(label)}
          {required && <InputRequired>*</InputRequired>}
        </InputTitle>
      )}
      {subLabel}
      <RHFSelect
        id={id}
        fullWidth
        name={name}
        value={value}
        style={label && { marginTop: '14px' }}
        InputLabelProps={{ shrink: true }}
        SelectProps={{
          native: false,
          sx: {
            textTransform: 'capitalize',
            color:
              value === 'placeholder'
                ? 'rgba(141, 138, 156, 1)'
                : 'unset',
            ...disabledStyle,
          },
        }}
        disabled={disabled}
        onChange={handleChange}
        onFocus={handleFocus}
        sx={sx}
        {...other}
      >
        {placeholder && (
          <MenuItem
            key="placeholder"
            value="placeholder"
            sx={{
              mx: 1,
              my: 0.5,
              borderRadius: 0.75,
              typography: 'body2',
              textTransform: 'capitalize',
            }}
          >
            {placeholder}
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem
            key={option.name}
            value={option.name}
            sx={{
              mx: 1,
              my: 0.5,
              borderRadius: 0.75,
              typography: 'body2',
              textTransform: 'capitalize',
            }}
          >
            <Box
              sx={{
                overflow: 'hidden',
                whiteSpace: 'wrap',
                textOverflow: 'ellipsis',
              }}
            >
              {t(option.label)}
            </Box>
          </MenuItem>
        ))}
      </RHFSelect>
    </>
  )
}

Select.defaultProps = {
  options: [],
  onChange: () => {},
  onFocus: () => {},
}
